import React, { useRef, useState } from 'react';
import { ActionIcon, Group, Menu, Tooltip, Box, Text, FileButton, rem } from '@mantine/core';
import {
  IconPhoto, IconLink, IconPalette, IconX, IconArrowUp, IconArrowDown, IconTrash,
} from '@tabler/icons-react';

// F 99.4 — re-export the canonical 12-gradient palette + helpers from
// coverResolver (which is the single source of truth). PageCover
// previously had its own duplicate copy of COVER_GRADIENTS (now removed).
export {
  COVER_GRADIENTS,
  resolveCover,
  classifyCover,
  getGradientForId,
  type CoverKind,
} from '../database/coverResolver';
import { COVER_GRADIENTS } from '../database/coverResolver';

/**
 * Aspect-ratio presets for the page cover.
 * - wide: 16:5 (Notion-style subtle band, ~200px tall on desktop)
 * - banner: 3:1 (taller, more dramatic, ~280px tall on desktop)
 * - none: no cover rendered
 */
export type CoverLayout = 'wide' | 'banner' | 'none';

const COVER_ASPECT: Record<Exclude<CoverLayout, 'none'>, number> = {
  wide: 16 / 5,
  banner: 3 / 1,
};

interface PageCoverProps {
  cover: string | undefined;             // current cover value (URL or data URL or gradient)
  coverLayout: CoverLayout;              // current layout
  coverPosition: number;                  // vertical position 0-100
  onChange: (cover: string | undefined, layout?: CoverLayout, position?: number) => void;
  canEdit: boolean;
}

/**
 * PageCover \u2014 a Notion-style cover that sits above the page title.
 *
 * Behaviour:
 * - When no cover: renders a thin "+ Add cover" button (hover-revealed)
 * - When cover is set: renders the cover image with a hover-revealed toolbar
 *   (Change | Reposition | Remove)
 * - Cover can be: a remote URL, a local file (uploaded as data URL), or
 *   one of 12 built-in gradients
 * - The vertical position is preserved so a 16:5 cover can be
 *   "panned" to show its top/middle/bottom
 *
 * Storage: the cover string is saved to localStorage as part of the
 * PageRef. For local files we keep the file as a base64 data URL \u2014
 * suitable for small images (< 2MB). Larger images should be replaced
 * with a URL.
 */
export const PageCover: React.FC<PageCoverProps> = ({
  cover, coverLayout, coverPosition, onChange, canEdit,
}) => {
  const fileResetRef = useRef<() => void>(null);
  const [urlInputOpen, setUrlInputOpen] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  if (coverLayout === 'none') return null;

  const aspect = COVER_ASPECT[coverLayout];

  // Determine if the cover is a remote URL (http/https) vs data URL vs gradient
  const isRemote = cover && /^https?:\/\//.test(cover);
  const isData = cover && cover.startsWith('data:');
  const isGradient = cover && cover.startsWith('linear-gradient');

  // Build the cover background style
  const coverStyle: React.CSSProperties = cover
    ? (isRemote || isData
        ? {
            backgroundImage: `url(${cover})`,
            backgroundSize: 'cover',
            backgroundPosition: `center ${coverPosition}%`,
            backgroundRepeat: 'no-repeat',
          }
        : { background: cover })
    : { background: 'var(--fc-bg-tertiary, #f1f1ef)' };

  const handleFile = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file (PNG, JPG, GIF, WebP, SVG).');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      alert('Image is over 4MB. For best results, use a smaller image or a hosted URL.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      onChange(dataUrl, coverLayout, 50);
    };
    reader.readAsDataURL(file);
  };

  const handleUrlSave = () => {
    const url = urlValue.trim();
    if (!url) return;
    onChange(url, coverLayout, 50);
    setUrlValue('');
    setUrlInputOpen(false);
  };

  if (!cover) {
    // No cover yet \u2014 render the "Add cover" affordance
    if (!canEdit) return null;
    return (
      <Box
        className="fc-page-cover fc-page-cover--add"
        style={{
          marginTop: -4,
          padding: '0 8px',
        }}
      >
        <Menu shadow="md" position="bottom-start" width={260}>
          <Menu.Target>
            <button
              type="button"
              className="fc-page-cover-add-btn"
              aria-label="Add cover"
            >
              <IconPhoto size={14} />
              <span>Add cover</span>
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            <FileButton resetRef={fileResetRef} onChange={handleFile} accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml">
              {(props) => (
                <Menu.Item leftSection={<IconPhoto size={14} />} {...props}>
                  Upload from device
                </Menu.Item>
              )}
            </FileButton>
            <Menu.Item
              leftSection={<IconLink size={14} />}
              onClick={() => setUrlInputOpen(true)}
            >
              Paste image URL
            </Menu.Item>
            <Menu.Divider />
            <Menu.Label>Gradients</Menu.Label>
            <Box px="xs" pb="xs">
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 6,
              }}>
                {COVER_GRADIENTS.map((g, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => onChange(g, coverLayout, 50)}
                    aria-label={`Gradient ${i + 1}`}
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      border: '1px solid var(--fc-border-secondary, #d4d4d1)',
                      borderRadius: 4,
                      cursor: 'pointer',
                      background: g,
                    }}
                  />
                ))}
              </div>
            </Box>
          </Menu.Dropdown>
        </Menu>
        {urlInputOpen && (
          <Box mt="xs" p="xs" style={{ background: 'var(--fc-bg-secondary)', borderRadius: 6 }}>
            <Group gap="xs">
              <input
                autoFocus
                placeholder="https://example.com/image.png"
                value={urlValue}
                onChange={(e) => setUrlValue(e.currentTarget.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleUrlSave(); if (e.key === 'Escape') setUrlInputOpen(false); }}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  border: '1px solid var(--fc-border-secondary)',
                  borderRadius: 4,
                  fontSize: 13,
                  background: 'var(--fc-bg-primary)',
                  color: 'var(--fc-text-primary)',
                }}
              />
              <button
                type="button"
                onClick={handleUrlSave}
                style={{ padding: '4px 10px', background: 'var(--mantine-color-blue-filled)', color: 'white', border: 'none', borderRadius: 4, fontSize: 13 }}
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => { setUrlInputOpen(false); setUrlValue(''); }}
                style={{ padding: '4px 8px', background: 'transparent', color: 'var(--fc-text-secondary)', border: 'none', fontSize: 13 }}
              >
                Cancel
              </button>
            </Group>
          </Box>
        )}
      </Box>
    );
  }

  // Cover is set \u2014 render the cover with the action toolbar
  return (
    <Box
      className="fc-page-cover"
      onDragOver={(e) => {
        if (!canEdit) return;
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setIsDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        // Only clear if leaving the cover (not entering a child)
        if (e.currentTarget === e.target) setIsDragOver(false);
      }}
      onDrop={(e) => {
        if (!canEdit) return;
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
      }}
      style={{
        position: 'relative',
        marginTop: 0,
        marginBottom: 12,
        borderRadius: coverLayout === 'banner' ? 8 : 6,
        overflow: 'hidden',
      }}
    >
      <div
        className="fc-page-cover-image"
        style={{
          width: '100%',
          aspectRatio: aspect,
          ...coverStyle,
          cursor: (isRemote || isData) ? 'grab' : 'default',
        }}
        role="img"
        aria-label={isRemote ? 'Page cover image' : 'Page cover'}
        onMouseDown={(e) => {
          if (!isRemote && !isData) return;
          e.preventDefault();
          const startY = e.clientY;
          const startPos = coverPosition;
          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
          // Convert vertical drag to position delta:
          // dragging up (negative dy) should INCREASE position (show bottom)
          // dragging down should DECREASE position (show top)
          const onMove = (ev: MouseEvent) => {
            const dy = ev.clientY - startY;
            // Map the full rect height to 0-100 position
            const deltaPct = -dy / rect.height * 100;
            const next = Math.max(0, Math.min(100, Math.round(startPos + deltaPct)));
            onChange(cover, coverLayout, next);
          };
          const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
          };
          document.body.style.cursor = 'grabbing';
          document.body.style.userSelect = 'none';
          document.addEventListener('mousemove', onMove);
          document.addEventListener('mouseup', onUp);
        }}
      />

      {isDragOver && (
        <div className="fc-page-cover-drop-overlay">
          <div className="fc-page-cover-drop-message">
            <IconPhoto size={32} />
            <Text size="sm" fw={600}>Drop image to set as cover</Text>
          </div>
        </div>
      )}

      {canEdit && (
        <div className="fc-page-cover-actions">
          <Group gap={4}>
            {/* Change \u2014 upload / URL / gradient picker */}
            <Menu shadow="md" position="bottom-start" width={260}>
              <Menu.Target>
                <ActionIcon
                  variant="filled"
                  color="dark"
                  size="sm"
                  aria-label="Change cover"
                  title="Change cover"
                >
                  <IconPhoto size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <FileButton resetRef={fileResetRef} onChange={handleFile} accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml">
                  {(props) => (
                    <Menu.Item leftSection={<IconPhoto size={14} />} {...props}>
                      Upload from device
                    </Menu.Item>
                  )}
                </FileButton>
                <Menu.Item leftSection={<IconLink size={14} />} onClick={() => setUrlInputOpen(true)}>
                  Paste image URL
                </Menu.Item>
                <Menu.Divider />
                <Menu.Label>Gradients</Menu.Label>
                <Box px="xs" pb="xs">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                    {COVER_GRADIENTS.map((g, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => onChange(g, coverLayout, 50)}
                        aria-label={`Gradient ${i + 1}`}
                        style={{
                          width: '100%', aspectRatio: '1',
                          border: '1px solid var(--fc-border-secondary, #d4d4d1)',
                          borderRadius: 4, cursor: 'pointer', background: g,
                        }}
                      />
                    ))}
                  </div>
                </Box>
              </Menu.Dropdown>
            </Menu>

            {/* Reposition \u2014 only meaningful for image (not gradient) */}
            {(isRemote || isData) && (
              <Tooltip label="Reposition">
                <ActionIcon.Group>
                  <ActionIcon
                    variant="filled"
                    color="dark"
                    size="sm"
                    onClick={() => onChange(cover, coverLayout, Math.max(0, coverPosition - 15))}
                    aria-label="Move cover up"
                  >
                    <IconArrowUp size={14} />
                  </ActionIcon>
                  <ActionIcon
                    variant="filled"
                    color="dark"
                    size="sm"
                    onClick={() => onChange(cover, coverLayout, Math.min(100, coverPosition + 15))}
                    aria-label="Move cover down"
                  >
                    <IconArrowDown size={14} />
                  </ActionIcon>
                </ActionIcon.Group>
              </Tooltip>
            )}

            {/* Layout toggle */}
            <Menu shadow="md" position="bottom-start">
              <Menu.Target>
                <ActionIcon
                  variant="filled"
                  color="dark"
                  size="sm"
                  aria-label="Cover layout"
                  title="Cover layout"
                >
                  <IconPalette size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Cover layout</Menu.Label>
                <Menu.Item onClick={() => onChange(cover, 'wide', coverPosition)}>
                  Wide (16:5)
                </Menu.Item>
                <Menu.Item onClick={() => onChange(cover, 'banner', coverPosition)}>
                  Banner (3:1)
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item color="red" leftSection={<IconX size={14} />} onClick={() => onChange(undefined, 'none', 50)}>
                  Hide cover
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>

            {/* Remove cover */}
            <Tooltip label="Remove cover">
              <ActionIcon
                variant="filled"
                color="red"
                size="sm"
                onClick={() => onChange(undefined, 'none', 50)}
                aria-label="Remove cover"
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </div>
      )}

      {urlInputOpen && (
        <Box p="xs" style={{ position: 'absolute', top: 8, left: 8, right: 8, background: 'var(--fc-bg-primary)', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          <Group gap="xs">
            <input
              autoFocus
              placeholder="https://example.com/image.png"
              value={urlValue}
              onChange={(e) => setUrlValue(e.currentTarget.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleUrlSave(); if (e.key === 'Escape') setUrlInputOpen(false); }}
              style={{ flex: 1, padding: '4px 8px', border: '1px solid var(--fc-border-secondary)', borderRadius: 4, fontSize: 13, background: 'var(--fc-bg-primary)', color: 'var(--fc-text-primary)' }}
            />
            <button type="button" onClick={handleUrlSave} style={{ padding: '4px 10px', background: 'var(--mantine-color-blue-filled)', color: 'white', border: 'none', borderRadius: 4, fontSize: 13 }}>Add</button>
            <button type="button" onClick={() => { setUrlInputOpen(false); setUrlValue(''); }} style={{ padding: '4px 8px', background: 'transparent', color: 'var(--fc-text-secondary)', border: 'none', fontSize: 13 }}>Cancel</button>
          </Group>
        </Box>
      )}
    </Box>
  );
};
